import { Button, Modal } from 'antd'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  type ConfirmButtonVariant,
  getConfirmVariantProps,
} from './confirmButtonVariant'

export interface ConfirmButtonProps {
  /** Text on the trigger button. */
  label: string
  /**
   * Runs once the user confirms. Owns its own error reporting — the dialog
   * closes whether it resolves or rejects.
   */
  onConfirm: () => void | Promise<void>
  variant?: ConfirmButtonVariant
  message?: string
  confirmLabel?: string
  /** Dialog heading. Defaults to the trigger's own label. */
  dialogTitle?: string
  disabled?: boolean
  loading?: boolean
  size?: 'small' | 'middle' | 'large'
}

export const ConfirmButton: FC<ConfirmButtonProps> = ({
  label,
  onConfirm,
  variant = 'neutral',
  message = 'Are you sure?',
  confirmLabel = 'Go Ahead',
  dialogTitle,
  disabled = false,
  loading = false,
  size = 'middle',
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    // Reset on setup so React 18 strict-mode's setup→cleanup→setup replay
    // doesn't leave the ref stuck at `false`.
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const openDialog = useCallback(() => setIsOpen(true), [])
  const closeDialog = useCallback(() => setIsOpen(false), [])

  const runConfirm = useCallback(async () => {
    setIsConfirming(true)
    try {
      await onConfirm()
    } finally {
      if (mountedRef.current) {
        setIsConfirming(false)
        setIsOpen(false)
      }
    }
  }, [onConfirm])

  const handleConfirm = useCallback(() => {
    runConfirm().catch(() => {
      // `onConfirm` owns error reporting; nothing to surface from here.
    })
  }, [runConfirm])

  const variantProps = useMemo(() => getConfirmVariantProps(variant), [variant])
  const mask = useMemo(() => ({ closable: !isConfirming }), [isConfirming])

  return (
    <>
      <Button
        {...variantProps}
        size={size}
        disabled={disabled}
        loading={loading}
        onClick={openDialog}>
        {label}
      </Button>
      <Modal
        title={dialogTitle ?? label}
        open={isOpen}
        onOk={handleConfirm}
        onCancel={closeDialog}
        okText={confirmLabel}
        okButtonProps={variantProps}
        confirmLoading={isConfirming}
        closable={!isConfirming}
        mask={mask}
        destroyOnHidden>
        {message}
      </Modal>
    </>
  )
}
